// 공통 낱말 — 제품군 어디서나 **같은 말이어야 하는** 것만 둔다.
//
// 서비스 사전이 펼쳐 쓴다:  export const ko = { ...commonKo, token: { … } } as const;
// 그러면 t("common.status.normal") 이 "정상" 이다. 서비스 사전에서 이 이름을 다른 말로 덮지
// 않는다 — 덮으면 「같은 상태를 서비스마다 다른 말로」가 다시 시작된다(COMMON.md 「화면 문구」).
//
// 낱말마다 붙은 주석은 **번역가·검토자에게 가는 설명**이다(어느 자리에 서는 말인가). 영어판은
// common.en.js, 타입은 common.ko.d.ts 가 리터럴로 들고 있다. 여기 낱말을 더하면 그 두 파일에도
// 같이 더한다(src/index.test.js 가 셋의 키가 같은지 본다).
//
// 더하는 기준: 서비스 **둘 이상**이 같은 뜻으로 이미 쓰는 낱말(README 「공통 낱말」). 한 서비스만 쓰면
// 그 서비스 사전에 둔다.

export const commonKo = {
  common: {
    /** 대상의 건강 상태 — 고정어. 「다운」·「오류」·「비정상」 같은 변형을 쓰지 않는다. */
    status: {
      /** 대상이 제대로 돈다. 상태 배지·표 칸 */
      normal: "정상",
      /** 돌지만 늦다(응답·수집이 기준보다 늦음) */
      delayed: "지연",
      /** 안 돈다·닿지 않는다 */
      outage: "장애",
      /** 사람이 일부러 멈춰 둔 상태 */
      maintenance: "점검 중",
      /** 값이 아직 한 번도 안 들어왔다 — 0 이 아니라 「모름」이다 */
      notCollected: "미수집",
    },
    /** 작업 진행 상태 — 건강 상태와 다른 축이다. 서버의 pending·completed 를 화면에 그대로 내지 않는다. */
    work: {
      /** 줄 서서 차례를 기다린다 */
      pending: "대기",
      /** 지금 돌고 있다 */
      running: "진행 중",
      /** 끝났고 잘 됐다 */
      success: "성공",
      /** 끝났고 안 됐다 */
      failed: "실패",
    },
    /** 흔한 단추 동사. 「확인」 한 단어로 뭉개지 않는다 — 무엇을 하는 단추인지 동사로 쓴다. */
    action: {
      /** 고친 것을 저장하는 단추 */
      save: "저장",
      /** 하던 것을 그만두고 닫는 단추(대화상자) */
      cancel: "취소",
      /** 지우는 단추 — 되돌릴 수 없으면 확인 대화상자가 앞에 선다 */
      delete: "삭제",
      /** 실패한 것을 한 번 더 하는 단추 */
      retry: "다시 시도",
      /** 창·판을 닫는 단추(아무것도 바꾸지 않는다) */
      close: "닫기",
      /** 고치기 화면을 여는 단추 */
      edit: "수정",
      /** 목록에 하나 더하는 단추 */
      add: "추가",
      /** 새것을 만드는 단추(토큰·계정 등) */
      create: "만들기",
      /** 화면 값을 다시 읽어 오는 단추 */
      refresh: "새로고침",
      /** 찾기 단추·입력칸 이름 */
      search: "검색",
      /** 글자를 클립보드로 복사하는 단추 */
      copy: "복사",
      /** 파일을 내 기기로 받는 단추 */
      download: "내려받기",
      /** 파일을 서버로 보내는 단추 */
      upload: "올리기",
      /** 고른 설정을 반영하는 단추 */
      apply: "적용",
      /** 값을 처음 상태로 되돌리는 단추 */
      reset: "초기화",
      /** 앞 화면으로 돌아가는 단추 */
      back: "뒤로",
      /** 다음 단계·쪽으로 가는 단추 */
      next: "다음",
      /** 앞 단계·쪽으로 가는 단추 */
      prev: "이전",
      /** 접힌 메뉴를 펴는 단추(폰 하단 탭바의 마지막 칸 등) */
      more: "더보기",
      /** 로그인 단추 */
      login: "로그인",
      /** 로그아웃 단추 */
      logout: "로그아웃",
      /** 단추가 하나뿐인 알림 창에서 「읽었다」를 뜻하는 단추(영어 OK). 할 일이 있는 단추에는 쓰지 않는다 — 그 동사를 쓴다 */
      ok: "확인",
      /** 펼친 덩어리를 접는 단추 */
      collapse: "접기",
      /** 접힌 덩어리를 펼치는 단추 */
      expand: "펼치기",
    },
    /** 칸·배지에 서는 상태 낱말(문장이 아니다). */
    state: {
      /** 값을 읽어 오는 동안 그 자리에 선다 */
      loading: "불러오는 중…",
      /** 저장 단추를 누른 뒤 끝날 때까지 단추 글자 */
      saving: "저장 중…",
      /** 만들기 단추를 누른 뒤 끝날 때까지 단추 글자 */
      creating: "만드는 중…",
      /** 값이 비었다(세어 봤더니 없음). 「모름」과 다르다 */
      none: "없음",
      /** 값을 읽지 못했다 — 0·「없음」으로 적지 않는다(할 일이 정반대다) */
      unknown: "알 수 없음",
      /** 켜져서 쓰이는 중(토큰·계정·규칙) */
      active: "활성",
      /** 꺼 두었다(지우지는 않았다) */
      inactive: "비활성",
    },
    /** 계정 역할 이름. */
    role: {
      /** 설정·계정을 다루는 역할 */
      admin: "관리자",
      /** 조직·프로젝트에 속해 일반 기능을 쓰는 역할 */
      member: "멤버",
    },
  },
};
